import { setISODay } from "date-fns";
import {
  colorByInvoiceStatus,
  colorByPaymentMode,
  colorByName,
  hex2Rgba,
  colorByInvoiceStatusForLineChart,
  hex2RGB,
  hexToRgba,
  getColorByHex
} from "./colorHelpers";

const calculateTotalCount = data => data.reduce((previousValue, current) => previousValue + current.count, 0);

const invoicesByStatusAdapter = invoicesByStatus => {
  const totalCount = calculateTotalCount(invoicesByStatus);
  const labels = [];
  const data = [];
  const colors = [];
  const hoverColors = [];

  invoicesByStatus.forEach(invoice => {
    if (invoice.status !== "DRAFT" && invoice.status !== "DELETED") {
      labels.push({ count: invoice.count, label: invoice.status });
      data.push(parseInt((invoice.count / totalCount) * 100, 10));
      const colorName = colorByInvoiceStatus(invoice.status);
      const color = getColorByHex(colorName);
      colors.push(color);
      hoverColors.push(hexToRgba(color, 0.6));
    }
  });
  return {
    invoicesByStatus: {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          hoverBackgroundColor: hoverColors
        }
      ]
    },
    totalInvoices: totalCount
  };
};

const invoicesByPaymentModeAdapter = invoicesByPaymentMode => {
  const totalCount = calculateTotalCount(invoicesByPaymentMode);
  const labels = [];
  const data = [];
  const colors = [];
  const hoverColors = [];

  invoicesByPaymentMode.forEach(invoice => {
    labels.push({ count: invoice.count, label: invoice.paymentMode });
    data.push(parseInt((invoice.count / totalCount) * 100, 10));
    const colorName = colorByPaymentMode(invoice.paymentMode);
    const color = getColorByHex(colorName);
    colors.push(color);
    hoverColors.push(hexToRgba(color, 0.6));
  });

  return {
    invoicesByPaymentMode: {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          hoverBackgroundColor: hoverColors
        }
      ]
    },
    totalPayments: totalCount
  };
};

function calculateTimeUnit(data) {
  const { length } = data;
  if (length <= 31) {
    return "day";
  }
  if (length > 31 && length < 91) {
    return "week";
  }
  if (length >= 91 && length < 730) {
    return "month";
  }
  return "year";
}

function calculateName(date, timeUnit) {
  if (timeUnit === "month") {
    return `${new Date(date.getFullYear(), date.getMonth(), 1)}`;
  }
  if (timeUnit === "week") {
    return `${setISODay(date, 1)}`;
  }
  if (timeUnit === "day") {
    return `${new Date(date.getFullYear(), date.getMonth(), date.getDate())}`;
  }

  return date.getFullYear();
}

function groupBy(data, timeUnit) {
  const grouped = {};

  data.forEach(a => {
    const date = new Date(a.date);
    const name = calculateName(date, timeUnit);
    if (grouped[name]) {
      grouped[name].y += a.count;
    } else {
      grouped[name] = {
        x: new Date(name),
        y: a.count
      };
    }
  });

  return Object.values(grouped);
}

function groupData(dataset) {
  if (dataset) {
    const timeUnit = calculateTimeUnit(dataset);
    const grouped = groupBy(dataset, timeUnit);
    return { data: grouped, timeUnit };
  }

  return { data: [] };
}

const invoicesByDateAdapter = invoicesByPaymentMode => {
  const datasets = [];
  let timeUnit;

  Object.keys(invoicesByPaymentMode).forEach(key => {
    const colorName = colorByInvoiceStatusForLineChart(key);
    const color = getColorByHex(colorName);

    const { data, timeUnit: unit } = groupData(invoicesByPaymentMode[key]);
    if (timeUnit !== "month") {
      timeUnit = unit;
    }

    datasets.push({
      data,
      label: key,
      backgroundColor: color,
      hoverBackgroundColor: hexToRgba(color, 0.6)
    });
  });

  return {
    datasets,
    timeUnit
  };
};

const dashboardDataAdapter = data => ({
  ...invoicesByStatusAdapter(data.invoicePieChart.invoicePieChartItems),
  ...invoicesByPaymentModeAdapter(data.paymentPieChart.paymentPieChartItems),
  invoicesByDate: invoicesByDateAdapter(data.lineChart.lineChartItems)
});

export { dashboardDataAdapter };
